import { useMemo, useState } from "react";
import { api } from "../api/client";
import type { StaffOrder } from "../api/types";
import { useAppState } from "../state/appState";
import { useSession } from "../state/session";
import { useToasts } from "../state/toasts";
import { Empty, Modal, useTicker } from "../ui/components";
import { TopBar } from "../ui/TopBar";
import { useAlarm } from "../ui/useAlarm";
import { CorrectionModal } from "./CorrectionModal";
import { CorrectedChip, MoneyLine, OrderLines, OrderNotes, PhoneLink, TicketHead, isOverdue } from "./OrderTicket";

/** Колонки повторяют производственный путь заказа (п. 11.1 ТЗ). */
const RAILS = [
  { status: "paid", title: "Новые", next: "accepted", action: "Принять" },
  { status: "accepted", title: "Приняты", next: "cooking", action: "Готовить" },
  { status: "cooking", title: "Готовятся", next: "ready", action: "Готов" },
  { status: "ready", title: "Готовы", next: null, action: null },
  { status: "picked_up", title: "У доставщика", next: null, action: null }
] as const;

export function KitchenScreen() {
  const { snapshot } = useAppState();
  const { can } = useSession();
  const { run } = useToasts();
  const now = useTicker();

  const [noteFor, setNoteFor] = useState<StaffOrder | null>(null);
  const [noteText, setNoteText] = useState("");
  const [correctFor, setCorrectFor] = useState<StaffOrder | null>(null);

  const orders = snapshot?.orders ?? [];

  // Очередь строится по времени подтверждённой оплаты, а не создания заказа (п. 12 ТЗ).
  const byStatus = useMemo(() => {
    const map = new Map<string, StaffOrder[]>();
    for (const rail of RAILS) {
      map.set(
        rail.status,
        orders
          .filter(order => order.status === rail.status)
          .sort((a, b) => new Date(a.paidAt ?? a.createdAt).getTime() - new Date(b.paidAt ?? b.createdAt).getTime())
      );
    }
    return map;
  }, [orders]);

  const pending = byStatus.get("paid")?.length ?? 0;
  const overdue = orders.filter(order => isOverdue(order, now)).length;
  const { enabled, enable } = useAlarm(pending);

  async function changeStatus(order: StaffOrder, status: string) {
    await run(() => api.once.patch(`/api/orders/${order.id}/status`, { status }));
  }

  async function hand(order: StaffOrder) {
    await run(() => api.once.post(`/api/orders/${order.id}/hand`), `Заказ №${order.number} выдан`);
  }

  async function saveNote() {
    if (!noteFor || !noteText.trim()) return;
    const ok = await run(() => api.post(`/api/orders/${noteFor.id}/note`, { text: noteText.trim() }), "Заметка добавлена");
    if (ok) {
      setNoteFor(null);
      setNoteText("");
    }
  }

  return (
    <div className="shell">
      <TopBar title="Кухня" subtitle="очередь заказов" />

      <main className="page">
        <div className="page-head">
          <div>
            <p className="eyebrow">Рабочая панель</p>
            <h1>Очередь кухни</h1>
          </div>
          <div className="timers">
            <span className="timer">
              в работе <b>{orders.filter(order => ["paid", "accepted", "cooking"].includes(order.status)).length}</b>
            </span>
            <span className={`timer ${overdue ? "late" : ""}`}>
              просрочено <b>{overdue}</b>
            </span>
          </div>
        </div>

        {!enabled ? (
          <div className="sound-nag">
            <div>
              <strong>Звук выключен</strong>
              <p className="tiny">Браузер разрешает звук только после нажатия. Сигнал повторяется, пока заказ не принят.</p>
            </div>
            <button type="button" className="btn" onClick={enable}>
              Включить звук
            </button>
          </div>
        ) : null}

        <div className="rails">
          {RAILS.map(rail => {
            const railOrders = byStatus.get(rail.status) ?? [];

            return (
              <section className="rail" key={rail.status}>
                <div className="rail-head">
                  <h2>{rail.title}</h2>
                  <span className="rail-count">{railOrders.length}</span>
                </div>

                {railOrders.length === 0 ? <Empty>Пусто</Empty> : null}

                {railOrders.map(order => (
                  <article className={`ticket ${isOverdue(order, now) ? "overdue" : ""}`} key={order.id}>
                    <TicketHead order={order} now={now} />
                    <CorrectedChip order={order} />
                    <OrderLines order={order} />
                    <OrderNotes order={order} />
                    <MoneyLine order={order} />
                    <PhoneLink order={order} />

                    <div className="ticket-actions">
                      {rail.next && can("orders.status.kitchen") ? (
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={() => void changeStatus(order, rail.next as string)}
                        >
                          {rail.action}
                        </button>
                      ) : null}

                      {/* Готовый заказ с окна закрывает тот, кто его отдал:
                          доставщика у него нет, и ждать его бессмысленно. */}
                      {order.status === "ready" && order.type === "pickup" && can("orders.status.kitchen") ? (
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={() => void hand(order)}
                        >
                          Выдать
                        </button>
                      ) : null}

                      {can("orders.note") ? (
                        <button type="button" className="btn btn-sm" onClick={() => setNoteFor(order)}>
                          Заметка
                        </button>
                      ) : null}

                      {can("orders.correct") ? (
                        <button type="button" className="btn btn-sm" onClick={() => setCorrectFor(order)}>
                          Изменить состав
                        </button>
                      ) : null}
                    </div>
                  </article>
                ))}
              </section>
            );
          })}
        </div>
      </main>

      {noteFor ? (
        <Modal
          title={`Заметка к заказу №${noteFor.number}`}
          onClose={() => setNoteFor(null)}
          actions={
            <>
              <button type="button" className="btn" onClick={() => setNoteFor(null)}>
                Отмена
              </button>
              <button type="button" className="btn btn-primary" onClick={() => void saveNote()}>
                Сохранить
              </button>
            </>
          }
        >
          <label className="field">
            <span>Текст заметки</span>
            <textarea
              autoFocus
              value={noteText}
              onChange={event => setNoteText(event.target.value)}
              placeholder="Клиент не отвечает / замена согласована / заказ задерживается"
            />
          </label>
          <p className="tiny muted">Заметку видят кухня, доставщик и администратор. Клиент её не видит.</p>
        </Modal>
      ) : null}

      {correctFor ? (
        <CorrectionModal
          order={orders.find(order => order.id === correctFor.id) ?? correctFor}
          onClose={() => setCorrectFor(null)}
        />
      ) : null}
    </div>
  );
}
